import React, { useRef } from "react";
import { useSelector } from "react-redux";
import { Link } from "@mui/material";
import { OpenInNew as OpenInNewIcon } from "@mui/icons-material";

import { fetchAccountDocumentPdf } from "app/services/accountDocument/accountDocument";
import { useGetAccountDocumentAvailabilityQuery } from "app/store/accountDocument/api/accountDocumentApi";
import { AVAILABILITY_FLAG } from "static/account_document";
import { EVOKE_ROLES, OBJECT_URL_REVOKE_DELAY_MS } from "static/constants";
import { loadPdfWithTitle } from "utils/pdf";
import { showLoadingInTab } from "utils/tabLoader";
import { showErrorToast } from "utils/toast";

const AccountDocumentLink = ({
  accountName,
  documentType,
  label,
  fontSize = "14px",
  color = "#175ec8",
  hasTrailingDivider = false,
}) => {
  const isOpening = useRef(false);

  const { currentUser: { role } = {} } = useSelector((state) => state.auth);
  const { accountsMapper } = useSelector((state) => state.account);

  const isEvokeRole = EVOKE_ROLES.includes(role);
  const accountId = accountsMapper?.[accountName]?._id;

  const { data: availability } = useGetAccountDocumentAvailabilityQuery(
    accountId,
    { skip: !isEvokeRole || !accountId }
  );

  if (
    !isEvokeRole ||
    !accountId ||
    availability?.[AVAILABILITY_FLAG[documentType]] !== true
  ) {
    return null;
  }

  const errorMessage = `Unable to open ${label}. Please try again.`;

  const handleClick = async () => {
    if (isOpening.current) {
      return;
    }

    // Open the tab synchronously, before awaiting, so popup blockers allow it.
    const tab = window.open("", "_blank");
    if (!tab) {
      showErrorToast(errorMessage);
      return;
    }
    const title = `${label} - ${accountName}`;

    isOpening.current = true;
    try {
      tab.document.title = title;
      showLoadingInTab(tab, `Loading ${label}...`);
      const response = await fetchAccountDocumentPdf(accountId, documentType);
      const file = response?.data;
      if (!(file instanceof Blob) || !file.type.startsWith("application/pdf")) {
        throw new Error("Unexpected response");
      }

      const objectUrl = URL.createObjectURL(
        await loadPdfWithTitle(file, title)
      );
      tab.location.replace(objectUrl);
      setTimeout(
        () => URL.revokeObjectURL(objectUrl),
        OBJECT_URL_REVOKE_DELAY_MS
      );
    } catch (error) {
      // No error message when the user closed the tab themselves while it loaded.
      const closedByUser = tab.closed;
      tab.close();
      if (!closedByUser) {
        showErrorToast(errorMessage);
      }
    } finally {
      isOpening.current = false;
    }
  };

  return (
    <>
      <Link
        component="button"
        type="button"
        underline="none"
        data-action={`${documentType}-link`}
        onClick={handleClick}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          color,
          fontWeight: 500,
          fontSize,
          cursor: "pointer",
        }}
      >
        {label}
        <OpenInNewIcon sx={{ fontSize: "inherit" }} />
      </Link>
      {hasTrailingDivider && (
        <span
          aria-hidden="true"
          style={{ alignSelf: "stretch", borderLeft: "1px solid #BDBDBD" }}
        />
      )}
    </>
  );
};

export default AccountDocumentLink;
